import { createRootRoute, createRoute, Outlet } from "@tanstack/react-router";
import { LoginPage } from "./pages/Login";
import { DashboardPage } from "./pages/Dashboard";
import { RelaySitesPage } from "./pages/RelaySites";
import { PushSitesPage } from "./pages/PushSites";
import { MainTemplatesPage } from "./pages/MainTemplates";
import { MainBindingsPage } from "./pages/MainBindings";
import { PushBindingsPage } from "./pages/PushBindings";
import { KeysPage } from "./pages/Keys";
import { SettingsPage } from "./pages/Settings";
import { DistributePage } from "./pages/Distribute";
import { HealthCheckPage } from "./pages/HealthCheck";
import { TaskListPage } from "./pages/TaskList";
import { UsagePage } from "./pages/Usage";
import { SuppliersPage } from "./pages/Suppliers";
import { UsersPage } from "./pages/Users";
import { SupplierDetailPage } from "./pages/SupplierDetail";
import { SupplierFingerprintConflictsPage } from "./pages/SupplierFingerprintConflicts";
import { AppShell } from "./components/AppShell";
import { SupplierAppShell } from "./components/supplier/SupplierAppShell";
import { SupplierLoginPage } from "./pages/supplier/Login";
import { SupplierDashboardPage } from "./pages/supplier/Dashboard";
import { SupplierDistributePage } from "./pages/supplier/Distribute";
import { SupplierUsagePage } from "./pages/supplier/Usage";
import { SupplierRecordsPage } from "./pages/supplier/Records";
import { SupplierSettingsPage } from "./pages/supplier/Settings";

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

// 供应商端入口/路由段(doc/046 §5.1, doc/052 §2.1):独立 login + 独立 shell,仅暴露
// dashboard / distribute / tasks / usage / settings 五个能力页。
const supplierLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/supplier/login",
  component: SupplierLoginPage,
});

const supplierShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "supplier-shell",
  component: () => (
    <SupplierAppShell>
      <Outlet />
    </SupplierAppShell>
  ),
});

const supplierDashboardRoute = createRoute({
  getParentRoute: () => supplierShellRoute,
  path: "/supplier/dashboard",
  component: SupplierDashboardPage,
});

const supplierDistributeRoute = createRoute({
  getParentRoute: () => supplierShellRoute,
  path: "/supplier/distribute",
  component: SupplierDistributePage,
});

const supplierTasksListRoute = createRoute({
  getParentRoute: () => supplierShellRoute,
  path: "/supplier/tasks/list",
  component: SupplierRecordsPage,
});

const supplierUsageRoute = createRoute({
  getParentRoute: () => supplierShellRoute,
  path: "/supplier/usage",
  component: SupplierUsagePage,
});

const supplierSettingsRoute = createRoute({
  getParentRoute: () => supplierShellRoute,
  path: "/supplier/settings",
  component: SupplierSettingsPage,
});

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "shell",
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const dashboardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/",
  component: DashboardPage,
});

const relaySitesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/relay-sites",
  component: RelaySitesPage,
});

const pushSitesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/push-sites",
  component: PushSitesPage,
});

const mainTemplatesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/templates",
  component: MainTemplatesPage,
});

const mainBindingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/relay-bindings",
  component: MainBindingsPage,
});

const pushBindingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/push-bindings",
  component: PushBindingsPage,
});

// /health-check — 粘贴检测(IT-23)。工作区导航「分发」前新增「检测」菜单。
const healthCheckRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/health-check",
  component: HealthCheckPage,
});

// /distribute — 主模板一键分发(005 合并单页)。旧的 /relay/distribute +
// /push/distribute 两页已合并为此单一入口;后端按 (site_type, code) 混合批分流。
const distributeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/distribute",
  component: DistributePage,
});

const keysRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/keys",
  validateSearch: (search: Record<string, unknown>): { key_id?: number } => {
    const id = Number(search.key_id);
    return Number.isSafeInteger(id) && id > 0 ? { key_id: id } : {};
  },
  component: KeysPage,
});

const usageRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/usage",
  component: UsagePage,
});

const settingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/settings",
  component: SettingsPage,
});

// /relay/tasks/list 与 /push/tasks/list — 分发明细审计页(侧边「分发记录」
// 入口),按 site_type 各一份。后端 /distributions 按 site_type 过滤。
// ?key_id= 由 /keys 带入;
// validateSearch 把它规整成可选 number。
const tasksListValidateSearch = (search: Record<string, unknown>): { key_id?: number } => {
  const raw = search.key_id;
  const n = raw == null || raw === "" ? undefined : Number(raw);
  return n != null && Number.isSafeInteger(n) && n > 0 ? { key_id: n } : {};
};

const relayTasksListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/relay/tasks/list",
  validateSearch: tasksListValidateSearch,
  component: () => <TaskListPage siteType="relay" />,
});

const pushTasksListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/push/tasks/list",
  validateSearch: tasksListValidateSearch,
  component: () => <TaskListPage siteType="push" />,
});

const suppliersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/suppliers",
  component: SuppliersPage,
});

// /users — 内部运营账号管理(IT-21),仅 admin 可操作。
const usersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/users",
  component: UsersPage,
});

const supplierDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/suppliers/$id",
  component: SupplierDetailPage,
});

const supplierFingerprintConflictsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/supplier-key-conflicts",
  component: SupplierFingerprintConflictsPage,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  supplierLoginRoute,
  supplierShellRoute.addChildren([
    supplierDashboardRoute,
    supplierDistributeRoute,
    supplierTasksListRoute,
    supplierUsageRoute,
    supplierSettingsRoute,
  ]),
  shellRoute.addChildren([
    dashboardRoute,
    relaySitesRoute,
    pushSitesRoute,
    mainTemplatesRoute,
    mainBindingsRoute,
    pushBindingsRoute,
    healthCheckRoute,
    distributeRoute,
    keysRoute,
    usageRoute,
    settingsRoute,
    suppliersRoute,
    usersRoute,
    supplierFingerprintConflictsRoute,
    supplierDetailRoute,
    relayTasksListRoute,
    pushTasksListRoute,
  ]),
]);
